/**
 * Bank Service
 * 
 * Service for handling bank-related API calls.
 */

import { apiConfig } from "@/config/api";
import { apiFetch } from "@/lib/apiFetch";

export interface Bank {
  _id: string;
  name: string;
  code: string;
  description?: string;
  status?: "pending" | "active" | "inactive";
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateBankInput {
  name: string;
  code: string;
  description?: string;
  status?: "pending" | "active" | "inactive";
  isActive?: boolean;
}

export interface UpdateBankInput {
  name?: string;
  code?: string;
  description?: string;
  status?: "pending" | "active" | "inactive";
  isActive?: boolean;
}

export interface BankListResponse {
  data: Bank[];
}

export interface BankResponse {
  data: Bank;
}

class BankServiceClass {
  private baseURL = `${apiConfig.baseURL}/api/banks`;

  async getAllBanks(isActive?: boolean): Promise<Bank[]> {
    // Handle both absolute and relative URLs
    let url: string;
    if (this.baseURL.startsWith('http://') || this.baseURL.startsWith('https://')) {
      // Absolute URL
      const urlObj = new URL(this.baseURL);
      if (isActive !== undefined) {
        urlObj.searchParams.append("isActive", String(isActive));
      }
      url = urlObj.toString();
    } else {
      // Relative URL - construct manually
      const params = new URLSearchParams();
      if (isActive !== undefined) {
        params.append("isActive", String(isActive));
      }
      const queryString = params.toString();
      url = queryString ? `${this.baseURL}?${queryString}` : this.baseURL;
    }

    const response = await apiFetch(url, {
      method: "GET",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: "Failed to fetch banks" }));
      throw new Error(error.error || `HTTP error! status: ${response.status}`);
    }

    const data: BankListResponse = await response.json();
    return data.data;
  }

  async getBankById(id: string): Promise<Bank> {
    const response = await apiFetch(`${this.baseURL}/${id}`, {
      method: "GET",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: "Failed to fetch bank" }));
      throw new Error(error.error || `HTTP error! status: ${response.status}`);
    }

    const data: BankResponse = await response.json();
    return data.data;
  }

  async createBank(input: CreateBankInput): Promise<Bank> {
    const response = await apiFetch(this.baseURL, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(input),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: "Failed to create bank" }));
      throw new Error(error.error || `HTTP error! status: ${response.status}`);
    }

    const data: BankResponse = await response.json();
    return data.data;
  }

  async updateBank(id: string, input: UpdateBankInput): Promise<Bank> {
    const response = await apiFetch(`${this.baseURL}/${id}`, {
      method: "PUT",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(input),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: "Failed to update bank" }));
      throw new Error(error.error || `HTTP error! status: ${response.status}`);
    }

    const data: BankResponse = await response.json();
    return data.data;
  }

  async deleteBank(id: string): Promise<Bank> {
    const response = await apiFetch(`${this.baseURL}/${id}`, {
      method: "DELETE",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: "Failed to delete bank" }));
      throw new Error(error.error || `HTTP error! status: ${response.status}`);
    }

    const data: BankResponse = await response.json();
    return data.data;
  }
}

export const bankService = new BankServiceClass();

